import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import type { ErpModule } from '@/api/platform.api';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { setWorkspaceModule } from '@/store/authSlice';
import { getLauncherModules } from '@/utils/moduleAccess';
import {
  canAccessWorkspacePath,
  consumePreSwitchPath,
  firstAccessibleModuleHome,
  MODULE_ACCESS_DENIED_EVENT,
  type ModuleAccessDeniedDetail,
} from '@/utils/moduleNavigation';
import { resolveSafeRevertPath } from '@/utils/routeAccess';
import { notify } from '@/core/toast/notify';
import { usePermissions } from '@/hooks/usePermissions';

/** Revert workspace navigation when the API reports a module is not enabled. */
export function useModuleAccessRecovery(activeModule: ErpModule | null) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const accessibleModules = useAppSelector((s) => s.auth.accessibleModules);
  const permissions = useAppSelector((s) => s.auth.user?.permissions ?? []);
  const roleCode = useAppSelector((s) => s.auth.user?.roleCode);
  const { has } = usePermissions();

  const launcherApps = useMemo(
    () => getLauncherModules(enabledModules, permissions, accessibleModules, roleCode, has),
    [enabledModules, permissions, accessibleModules, roleCode, has],
  );

  const launcherCodes = useMemo(
    () => launcherApps.map((entry) => entry.erpModule),
    [launcherApps],
  );

  const canAccessPath = useMemo(
    () => (pathname: string) =>
      canAccessWorkspacePath(pathname, enabledModules, launcherCodes, accessibleModules),
    [accessibleModules, enabledModules, launcherCodes],
  );

  useEffect(() => {
    const onDenied = (event: Event) => {
      const detail = (event as CustomEvent<ModuleAccessDeniedDetail>).detail;
      if (!detail?.message) return;

      notify.error(detail.message);

      const blockedPath = location.pathname;
      const activeHome =
        (activeModule && getModuleEntryByErpModule(activeModule)?.homePath) ||
        firstAccessibleModuleHome(launcherCodes) ||
        '/pos/dashboard';

      const preSwitch = consumePreSwitchPath();
      const revertTo = resolveSafeRevertPath(blockedPath, activeHome, {
        canAccess: canAccessPath,
        extraCandidates: preSwitch ? [preSwitch] : [],
      });

      if (!revertTo) return;

      const current = `${location.pathname}${location.search}`;
      if (revertTo === current) return;

      const module = resolveModuleFromPath(revertTo.split('?')[0]);
      if (module) {
        dispatch(setWorkspaceModule(module));
      }
      navigate(revertTo, { replace: true });
    };

    window.addEventListener(MODULE_ACCESS_DENIED_EVENT, onDenied);
    return () => window.removeEventListener(MODULE_ACCESS_DENIED_EVENT, onDenied);
  }, [
    activeModule,
    canAccessPath,
    dispatch,
    launcherCodes,
    location.pathname,
    location.search,
    navigate,
  ]);

  return { canAccessPath, launcherCodes };
}
